import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../context/AdminContext";
import { toast } from "sonner";
import axios from "axios";
import {
  CreditCard, ArrowLeft, Crown, DollarSign, Users, TrendingUp, LogOut, Calendar
} from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

const AdminMemberships = () => {
  const { token, logout } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/memberships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load membership data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-bold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-400" />
                Membership Management
              </h1>
              <p className="text-sm text-slate-400">{data?.stats?.total_premium || 0} premium members</p>
            </div>
          </div>
          <button
            onClick={() => { logout(); toast.success("Logged out"); }}
            className="p-2 text-slate-400 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
              <Crown className="w-6 h-6 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-white">{data?.stats?.total_premium || 0}</p>
            <p className="text-slate-400 text-sm">Total Premium</p>
          </div>

          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-indigo-400" />
            </div>
            <p className="text-3xl font-bold text-white">{data?.stats?.monthly || 0}</p>
            <p className="text-slate-400 text-sm">Monthly Plans</p>
          </div>

          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{data?.stats?.yearly || 0}</p>
            <p className="text-slate-400 text-sm">Yearly Plans</p>
          </div>

          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">{data?.stats?.admin_granted || 0}</p>
            <p className="text-slate-400 text-sm">Admin Granted</p>
          </div>
        </div>

        {/* Current Plans */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 mb-8">
          <h3 className="font-bold text-white mb-4">Current Pricing</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-700/50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300 font-medium">Monthly Plan</span>
                <span className="text-2xl font-bold text-white">${data?.plans?.monthly?.price || 9.99}</span>
              </div>
              <p className="text-slate-400 text-sm">Per month</p>
            </div>
            <div className="p-4 bg-slate-700/50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300 font-medium">Yearly Plan</span>
                <span className="text-2xl font-bold text-white">${data?.plans?.yearly?.price || 79.00}</span>
              </div>
              <p className="text-slate-400 text-sm">Per year (Save 34%)</p>
            </div>
          </div>
        </div>

        {/* Premium Users */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden mb-8">
          <div className="p-6 border-b border-slate-700">
            <h3 className="font-bold text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              Premium Members
            </h3>
          </div>
          
          {(data?.premium_users || []).length === 0 ? (
            <div className="p-8 text-center text-slate-400">No premium members yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">User</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Plan</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Subscribed</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Expires</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {(data?.premium_users || []).map((user) => (
                    <tr key={user.id} className="hover:bg-slate-700/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">
                              {user.name?.charAt(0).toUpperCase() || "?"}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-slate-400 text-sm">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                          user.membership?.plan === "yearly" 
                            ? "bg-green-500/20 text-green-400" 
                            : user.membership?.plan === "admin_granted"
                            ? "bg-purple-500/20 text-purple-400"
                            : "bg-indigo-500/20 text-indigo-400"
                        }`}>
                          {user.membership?.plan_name || user.membership?.plan || "Premium"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {user.membership?.subscribed_at 
                            ? new Date(user.membership.subscribed_at).toLocaleDateString() 
                            : "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {user.membership?.expires_at 
                            ? new Date(user.membership.expires_at).toLocaleDateString() 
                            : "Never"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-700">
            <h3 className="font-bold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Recent Transactions
            </h3>
          </div>
          
          {(data?.transactions || []).length === 0 ? (
            <div className="p-8 text-center text-slate-400">No transactions yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Session ID</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Plan</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Amount</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Status</th>
                    <th className="text-left px-6 py-4 text-slate-300 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {(data?.transactions || []).slice(0, 20).map((tx) => (
                    <tr key={tx.id || tx.session_id} className="hover:bg-slate-700/30">
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm font-mono">
                          {tx.session_id?.substring(0, 20)}...
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 capitalize">{tx.plan_id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">${tx.amount}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                          tx.payment_status === "paid" 
                            ? "bg-green-500/20 text-green-400" 
                            : "bg-amber-500/20 text-amber-400"
                        }`}>
                          {tx.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMemberships;
